import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaSpinner,
  FaSave,
  FaCheckCircle,
  FaUsers,
  FaClock,
  FaChartBar,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type StudentScore = {
  attemptId: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  lesenScore: number;
  lesenMaxScore: number;
  horenScore: number;
  horenMaxScore: number;
  schreibenScore: number;
  schreibenMaxScore: number;
  sprechenScore: number;
  sprechenMaxScore: number;
  totalScore: number;
  maxScore: number;
  percentage: number;
  resultsPublished: boolean;
  hasWritingTask: boolean;
};

type BatchInfo = {
  batchName: string;
  examSetName: string;
  level: string;
  studentCount: number;
};

const AdminBatchGrading: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();

  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [students, setStudents] = useState<StudentScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal states
  const [showWritingModal, setShowWritingModal] = useState(false);
  const [selectedStudentForWriting, setSelectedStudentForWriting] =
    useState<StudentScore | null>(null);
  const [writingAnswers, setWritingAnswers] = useState<any[]>([]);

  useEffect(() => {
    fetchBatchData();
  }, [assignmentId]);

  const fetchBatchData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/assignments/${assignmentId}/attempts`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setBatchInfo(data.batchInfo || data.data?.batchInfo);

        const attemptsData =
          data.attempts || data.data?.attempts || data.data || [];

        // Transform to StudentScore format
        const scoresData: StudentScore[] = attemptsData.map((attempt: any) => ({
          attemptId: attempt.id,
          studentId: attempt.studentId,
          studentName: attempt.studentName,
          studentEmail: attempt.studentEmail,
          lesenScore: attempt.lesenScore || 0,
          lesenMaxScore: attempt.lesenMaxScore || 25,
          horenScore: attempt.horenScore || 0,
          horenMaxScore: attempt.horenMaxScore || 25,
          schreibenScore: attempt.schreibenScore || 0,
          schreibenMaxScore: attempt.schreibenMaxScore || 25,
          sprechenScore: attempt.sprechenScore || 0,
          sprechenMaxScore: attempt.sprechenMaxScore || 15,
          totalScore: attempt.totalScore || 0,
          maxScore: attempt.maxScore || 100,
          percentage: attempt.percentage || 0,
          resultsPublished: attempt.resultsPublished || false,
          hasWritingTask: (attempt.schreibenMaxScore || 0) > 0,
        }));

        setStudents(scoresData);
      } else {
        setError("Failed to load batch data");
      }
    } catch (err) {
      console.error("Error fetching batch data:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (
    attemptId: number,
    field: "lesenScore" | "horenScore" | "schreibenScore" | "sprechenScore",
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;

    setStudents((prev) =>
      prev.map((student) => {
        if (student.attemptId === attemptId) {
          const updated = { ...student, [field]: numValue };

          // Recalculate total
          updated.totalScore =
            updated.lesenScore +
            updated.horenScore +
            updated.schreibenScore +
            updated.sprechenScore;

          // ✅ FIXED: maxScore already includes Sprechen
          updated.percentage =
            updated.maxScore > 0
              ? (updated.totalScore / updated.maxScore) * 100
              : 0;

          return updated;
        }
        return student;
      })
    );
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Save each student's scores
      const savePromises = students.map((student) =>
        fetch(
          `${API_BASE_URL}/api/admin/exams/attempts/${student.attemptId}/scores`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              lesenScore: student.lesenScore,
              horenScore: student.horenScore,
              schreibenScore: student.schreibenScore,
              sprechenScore: student.sprechenScore,
              sprechenMaxScore: student.sprechenMaxScore,
            }),
          }
        )
      );

      const results = await Promise.all(savePromises);
      const allSuccessful = results.every((res) => res.ok);

      if (allSuccessful) {
        setSuccess("✅ All scores saved successfully!");
        await fetchBatchData();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError("Some scores failed to save. Please try again.");
      }
    } catch (err) {
      console.error("Error saving scores:", err);
      setError("Network error while saving");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishBatch = async () => {
    const unpublishedCount = students.filter((s) => !s.resultsPublished).length;

    if (unpublishedCount === 0) {
      alert("All results are already published!");
      return;
    }

    if (
      !window.confirm(
        `Publish results for ${unpublishedCount} student(s)?\n\n` +
          `They will be able to view their complete exam results.`
      )
    ) {
      return;
    }

    setPublishing(true);
    setError("");
    setSuccess("");

    try {
      const publishPromises = students
        .filter((s) => !s.resultsPublished)
        .map((student) =>
          fetch(
            `${API_BASE_URL}/api/admin/exams/attempts/${student.attemptId}/publish-results`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                message: "Your exam results are now available!",
              }),
            }
          )
        );

      const results = await Promise.all(publishPromises);
      const allSuccessful = results.every((res) => res.ok);

      if (allSuccessful) {
        setSuccess(`🎉 Results published for ${unpublishedCount} student(s)!`);
        await fetchBatchData();
        setTimeout(() => setSuccess(""), 5000);
      } else {
        setError("Some results failed to publish. Please try again.");
      }
    } catch (err) {
      console.error("Error publishing results:", err);
      setError("Network error while publishing");
    } finally {
      setPublishing(false);
    }
  };

  const allScoresEntered = (student: StudentScore) => {
    return (
      student.lesenScore > 0 &&
      student.horenScore > 0 &&
      student.schreibenScore > 0 &&
      student.sprechenScore > 0
    );
  };

  // Open writing modal
  const handleViewWriting = async (student: StudentScore) => {
    setSelectedStudentForWriting(student);
    setShowWritingModal(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/attempts/${student.attemptId}/answers`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        const allAnswers = data.data || data;

        // Filter only writing tasks
        const writing = allAnswers.filter((a: any) =>
          ["ESSAY", "EMAIL", "LETTER"].includes(a.questionType)
        );

        setWritingAnswers(writing);
      }
    } catch (err) {
      console.error("Error fetching writing tasks:", err);
    }
  };

  // Grade writing answer
  const handleGradeWriting = async (answerId: number, score: number) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/answers/${answerId}/grade`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pointsAwarded: score, feedback: "" }),
        }
      );

      if (response.ok) {
        setSuccess("Answer graded successfully");
        // Refresh writing answers
        if (selectedStudentForWriting) {
          await handleViewWriting(selectedStudentForWriting);
        }
        // Refresh batch data
        await fetchBatchData();
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Error grading:", err);
      setError("Failed to grade answer");
    } finally {
      setSaving(false);
    }
  };

  // AI evaluate single writing answer
  const handleAIEvaluateAnswer = async (answerId: number) => {
    if (!window.confirm("Use AI to evaluate this answer?")) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Use batch endpoint
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/attempts/${selectedStudentForWriting?.attemptId}/evaluate-ai-batch`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        setSuccess("AI evaluation completed!");
        // Refresh writing answers
        if (selectedStudentForWriting) {
          await handleViewWriting(selectedStudentForWriting);
        }
        // Refresh batch data
        await fetchBatchData();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const data = await response.json();
        setError(data.message || "AI evaluation failed");
      }
    } catch (err) {
      console.error("AI evaluation error:", err);
      setError("Failed to evaluate with AI");
    } finally {
      setSaving(false);
    }
  };

  const readyToPublishCount = students.filter(
    (s) => allScoresEntered(s) && !s.resultsPublished
  ).length;

  const allPublished = students.every((s) => s.resultsPublished);
  const canPublish = readyToPublishCount > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading batch data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaUsers />}
        title="Batch Grading"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: "Assignments", to: "/admin/exams/assignments" },
          {
            label: batchInfo
              ? `${batchInfo.batchName} • ${batchInfo.examSetName} (${batchInfo.level})`
              : "Grade",
          },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <FaCheckCircle />
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  Total Students
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {students.length}
                </p>
              </div>
              <FaUsers className="text-blue-600 text-2xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  Ready to Publish
                </p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {readyToPublishCount}
                </p>
              </div>
              <FaCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Published</p>
                <p className="text-2xl font-bold text-[#009fe3] mt-1">
                  {students.filter((s) => s.resultsPublished).length}
                </p>
              </div>
              <FaChartBar className="text-[#009fe3] text-2xl" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Pending</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  {
                    students.filter(
                      (s) => !allScoresEntered(s) && !s.resultsPublished
                    ).length
                  }
                </p>
              </div>
              <FaClock className="text-orange-600 text-2xl" />
            </div>
          </div>
        </div>

        {/* Grading Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          {students.length === 0 ? (
            <div className="text-center py-12">
              <FaUsers className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Submissions Yet
              </h3>
              <p className="text-gray-500 text-sm">
                Students haven't submitted this exam yet
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Student
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      📖 Lesen
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      🎧 Hören
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      ✍️ Schreiben
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      🗣️ Sprechen
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Total
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {students.map((student, index) => (
                    <tr
                      key={student.attemptId}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      {/* Student Name */}
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {student.studentName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {student.studentEmail}
                          </div>
                        </div>
                      </td>

                      {/* Lesen Score */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            value={student.lesenScore}
                            onChange={(e) =>
                              handleScoreChange(
                                student.attemptId,
                                "lesenScore",
                                e.target.value
                              )
                            }
                            min="0"
                            max={student.lesenMaxScore}
                            step="0.5"
                            className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            style={{ fontSize: "14px" }}
                          />
                          <span className="text-gray-600 text-sm">
                            /{student.lesenMaxScore}
                          </span>
                        </div>
                      </td>

                      {/* Hören Score */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            value={student.horenScore}
                            onChange={(e) =>
                              handleScoreChange(
                                student.attemptId,
                                "horenScore",
                                e.target.value
                              )
                            }
                            min="0"
                            max={student.horenMaxScore}
                            step="0.5"
                            className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            style={{ fontSize: "14px" }}
                          />
                          <span className="text-gray-600 text-sm">
                            /{student.horenMaxScore}
                          </span>
                        </div>
                      </td>

                      {/* Schreiben Score */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            value={student.schreibenScore}
                            onChange={(e) =>
                              handleScoreChange(
                                student.attemptId,
                                "schreibenScore",
                                e.target.value
                              )
                            }
                            min="0"
                            max={student.schreibenMaxScore}
                            step="0.5"
                            className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            style={{ fontSize: "14px" }}
                          />
                          <span className="text-gray-600 text-sm">
                            /{student.schreibenMaxScore}
                          </span>
                        </div>
                      </td>

                      {/* Sprechen Score */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            value={student.sprechenScore || ""}
                            onChange={(e) =>
                              handleScoreChange(
                                student.attemptId,
                                "sprechenScore",
                                e.target.value
                              )
                            }
                            min="0"
                            max={student.sprechenMaxScore}
                            step="0.5"
                            placeholder="0"
                            className={`w-16 px-2 py-1 text-center border rounded focus:ring-2 focus:border-transparent ${
                              student.sprechenScore > 0
                                ? "border-gray-300 focus:ring-orange-500"
                                : "border-orange-400 bg-orange-50 focus:ring-orange-500"
                            }`}
                            style={{ fontSize: "14px" }}
                          />
                          <span className="text-gray-600 text-sm">
                            /{student.sprechenMaxScore}
                          </span>
                        </div>
                      </td>

                      {/* Total Score - ✅ FIXED */}
                      <td className="px-4 py-3 text-center">
                        <div>
                          <div className="text-lg font-bold text-gray-900">
                            {student.totalScore.toFixed(1)}
                          </div>
                          <div className="text-xs text-gray-600">
                            /{student.maxScore}
                          </div>
                          <div className="text-xs font-semibold text-[#009fe3]">
                            {student.percentage.toFixed(1)}%
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        {student.resultsPublished ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            <FaCheckCircle size={10} />
                            Published
                          </span>
                        ) : allScoresEntered(student) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                            <FaCheckCircle size={10} />
                            Ready
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                            <FaClock size={10} />
                            Pending
                          </span>
                        )}
                      </td>

                      {/* Actions - View Writing Button */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewWriting(student)}
                          disabled={!student.hasWritingTask}
                          className={`inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${
                            student.hasWritingTask
                              ? "bg-[#009fe3] text-white hover:bg-cyan-600 cursor-pointer"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                          }`}
                          title={
                            student.hasWritingTask
                              ? "View writing tasks"
                              : "No writing task submitted"
                          }
                        >
                          📝 Writing
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {students.length > 0 && (
          <div className="flex gap-4">
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#009fe3] text-white rounded-lg font-bold text-lg hover:bg-cyan-600 disabled:opacity-50 transition-all shadow-lg"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save All Changes
                </>
              )}
            </button>

            <button
              onClick={handlePublishBatch}
              disabled={publishing || !canPublish}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold text-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {publishing ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  Publish Results for Batch
                  {canPublish && (
                    <span className="ml-1 px-2 py-0.5 bg-white/30 rounded text-sm">
                      {readyToPublishCount}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>
              • Edit scores directly in the table (all modules are editable)
            </li>
            <li>• Sprechen score is required for publishing results</li>
            <li>• Click "Save All Changes" to save your edits</li>
            <li>
              • Click "Publish Results for Batch" when all scores are complete
            </li>
            <li>• Students can view results immediately after publishing</li>
          </ul>
        </div>
      </main>

      {/* Writing Tasks Modal */}
      {showWritingModal && selectedStudentForWriting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  📝 Writing Tasks
                </h2>
                <p className="text-sm text-white/90 mt-1">
                  {selectedStudentForWriting.studentName} -{" "}
                  {selectedStudentForWriting.studentEmail}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowWritingModal(false);
                  setSelectedStudentForWriting(null);
                  setWritingAnswers([]);
                }}
                className="text-white hover:text-gray-200 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {writingAnswers.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No writing tasks found</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {writingAnswers.map((answer) => (
                    <WritingAnswerCard
                      key={answer.id}
                      answer={answer}
                      onGrade={handleGradeWriting}
                      onAIEvaluate={handleAIEvaluateAnswer}
                      saving={saving}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <button
                onClick={() => {
                  setShowWritingModal(false);
                  setSelectedStudentForWriting(null);
                  setWritingAnswers([]);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// WritingAnswerCard Component
// ========================================
const WritingAnswerCard: React.FC<{
  answer: any;
  onGrade: (answerId: number, score: number) => Promise<void>;
  onAIEvaluate: (answerId: number) => Promise<void>;
  saving: boolean;
}> = ({ answer, onGrade, onAIEvaluate, saving }) => {
  const [score, setScore] = useState(answer.score?.toString() || "");
  const [isEditing, setIsEditing] = useState(!answer.isGraded);

  // Update score when answer changes (e.g., after AI evaluation)
  useEffect(() => {
    const newScore = answer.pointsEarned ?? answer.score;
    if (newScore !== undefined && newScore !== null) {
      setScore(newScore.toString());
    }
  }, [answer.pointsEarned, answer.score, answer.id]);

  const handleSubmit = async () => {
    await onGrade(answer.id, parseFloat(score) || 0);
    setIsEditing(false);
  };

  const getTypeColor = () => {
    switch (answer.questionType) {
      case "ESSAY":
        return "bg-purple-100 text-purple-800 border-purple-300";
      case "EMAIL":
        return "bg-pink-100 text-pink-800 border-pink-300";
      case "LETTER":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getPoints = () => {
    return answer.points ?? answer.maxPoints ?? answer.pointsPossible ?? 0;
  };

  const pointsValue = getPoints();

  return (
    <div
      className={`border-2 rounded-lg overflow-hidden ${
        answer.isGraded ? "border-green-500" : "border-orange-500"
      }`}
    >
      {/* Header */}
      <div className={`px-4 py-3 ${getTypeColor()} border-b`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-bold">Q{answer.questionNumber}</span>
            <span className="text-xs font-semibold">{answer.questionType}</span>
          </div>
          <span className="text-sm font-semibold">
            {pointsValue > 0 ? `${pointsValue} points` : "Points N/A"}
          </span>
        </div>
      </div>

      {/* Question */}
      <div className="px-4 py-3 bg-blue-50 border-b">
        <p className="text-sm font-semibold text-gray-700 mb-1">Question:</p>
        <p className="text-sm text-gray-900">{answer.questionText}</p>
      </div>

      {/* Student Answer */}
      <div className="px-4 py-3 bg-white">
        <p className="text-sm font-semibold text-gray-700 mb-2">
          Student's Answer:
        </p>
        <div className="bg-gray-50 rounded p-3 border border-gray-200 max-h-64 overflow-y-auto">
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {answer.studentAnswer || (
              <span className="text-gray-400 italic">No answer</span>
            )}
          </p>
        </div>
        {answer.studentAnswer && (
          <p className="text-xs text-gray-500 mt-2">
            Words: {answer.studentAnswer.split(/\s+/).length}
          </p>
        )}
      </div>

      {/* AI Feedback Display */}
      {answer.feedback && answer.gradedBy === "AI" && (
        <div className="px-4 py-3 bg-gradient-to-br from-purple-50 to-pink-50 border-t border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🤖</span>
            <p className="text-sm font-bold text-purple-900">
              AI Evaluation Feedback
            </p>
          </div>
          <div className="bg-white/80 rounded-lg p-3 text-sm space-y-3">
            <div className="prose prose-sm max-w-none">
              {answer.feedback.split("\n").map((line: string, idx: number) => {
                // Parse markdown-style formatting
                if (line.startsWith("**") && line.endsWith("**")) {
                  return (
                    <p key={idx} className="font-semibold text-gray-900 mb-1">
                      {line.replace(/\*\*/g, "")}
                    </p>
                  );
                } else if (line.startsWith("- ") || line.startsWith("• ")) {
                  return (
                    <li key={idx} className="ml-4 text-gray-700">
                      {line.substring(2)}
                    </li>
                  );
                } else if (
                  line.startsWith("✓ ") ||
                  line.startsWith("⚠ ") ||
                  line.startsWith("💡 ")
                ) {
                  return (
                    <p key={idx} className="text-gray-700 ml-2">
                      {line}
                    </p>
                  );
                } else if (line.trim() === "---") {
                  return <hr key={idx} className="my-2 border-gray-300" />;
                } else if (line.trim()) {
                  return (
                    <p key={idx} className="text-gray-700">
                      {line}
                    </p>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      )}

      {/* Regular Feedback Display (non-AI) */}
      {answer.feedback && answer.gradedBy !== "AI" && (
        <div className="px-4 py-3 bg-blue-50 border-t">
          <p className="text-sm font-semibold text-gray-700 mb-2">
            Mentor Feedback:
          </p>
          <div className="bg-white rounded p-3 text-sm text-gray-700 whitespace-pre-wrap">
            {answer.feedback}
          </div>
        </div>
      )}

      {/* Grading */}
      <div className="px-4 py-3 bg-gray-50 border-t">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Score{pointsValue > 0 ? ` (out of ${pointsValue})` : ""}:
            </label>
            <input
              type="number"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              min="0"
              max={pointsValue || 100}
              step="0.5"
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#009fe3] disabled:bg-gray-100 text-sm"
            />
          </div>

          {/* AI Evaluate Button */}
          <div className="flex items-end">
            <button
              onClick={() => onAIEvaluate(answer.id)}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded font-semibold hover:shadow-lg disabled:opacity-50 text-sm flex items-center gap-1"
              title="Use AI to evaluate this answer"
            >
              🤖 AI
            </button>
          </div>

          <div className="flex items-end">
            {isEditing ? (
              <button
                onClick={handleSubmit}
                disabled={saving || !score}
                className="px-6 py-2 bg-green-600 text-white rounded font-semibold hover:bg-green-700 disabled:opacity-50 text-sm flex items-center justify-center gap-1"
              >
                <FaSave size={12} />
                Save
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-2 bg-[#009fe3] text-white rounded font-semibold hover:bg-cyan-600 text-sm"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBatchGrading;
